// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import baseTheme from "@insite/mobius/globals/baseTheme";
import { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import ChevronsUpDown from "@insite/mobius/Icons/ChevronsUpDown";
import { LinkPresentationProps, StyledClickable, StyledTypography } from "@insite/mobius/Link";
import { LoadingSpinnerStyle } from "@insite/mobius/LoadingSpinner";
import { OverflowMenuPresentationProps } from "@insite/mobius/OverflowMenu";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { breakpointMediaQueries, get, getProp, injectCss, resolveColor } from "@insite/mobius/utilities";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import { FormFieldClickable } from "@steiner/MobiusOverrides/FormField";
import { TextFieldPresentationProps, TextFieldProps } from "@steiner/MobiusOverrides/TextField";
import Search from "@steiner/XDL/Icons/Search";
import { fontConfig } from "@steiner/XDL/PreStyle/typography";
import { color, mergeWithCss, styledScrollbar, xScreen } from "@steiner/XDL/Utilities";
import { BreakpointKey, breakpointKeys } from "@steiner/XDL/Utilities/breakpointMediaQueries";
import styled, { css } from "styled-components";

export const hideElement = css`
    &&&& {
        display: none;
        @media print {
            display: none;
        }
    }
`;

export const hideElementOnPrint = css`
    &&&& {
        @media print {
            display: none;
        }
    }
`;

export const showElementOnPrintOnly = css`
    &&&& {
        display: none;
        @media print {
            display: block;
        }
    }
`;

export const displayFlexOnPrint = css`
    &&&& {
        @media print {
            display: flex;
            margin-right: 0;
        }
    }
`;

export const avoidBreakInsidePrintElement = css`
    @media print {
        -webkit-column-break-inside: avoid;
        page-break-inside: avoid;
        break-inside: avoid;
    }
`;

export const styledLinksMenu = (width: number) => {
    return ({ theme }: any) => css`
        ${getProp("theme.tooltip.defaultProps.cssOverrides.tooltipContainer")}

        background-color: ${resolveColor("common.white", theme)};
        box-shadow: ${({ theme }) => get(theme, "shadows.2")};
        border: 0.8px solid ${resolveColor("common.ui3", theme)};
        border-radius: 4px;
        width: ${width}px;
        display: flex;
        flex-direction: column;
        max-height: 190px;

        ${StyledClickable} {
            display: flex;
            width: 100%;
            ${/* sc-selector */ StyledTypography} {
                font-size: 12px;
                padding: 10px 15px;
                color: ${resolveColor("text", theme)};
                font-weight: normal;
                text-transform: none;
                letter-spacing: 0;
                width: 100%;
            }
            &:not(:disabled):not([disabled]):hover ${/* sc-selector */ StyledTypography} {
                background: ${resolveColor("common.ui2", theme)};
                color: ${resolveColor("text", theme)};
            }
        }

        ${styledScrollbar()}
    `;
};

export const layoutContainer = css`
    @media print {
        max-width: 100% !important;
        margin: 0 !important;
    }

    ${({ theme }: any) => {
        const { maxWidths }: { maxWidths: number[] } = theme.breakpoints || baseTheme.breakpoints;
        const { containerPadding = [16] }: { containerPadding: number[] } = theme.breakpoints;

        const containerStyles = maxWidths.map(
            (maxWidth, index) =>
                css`
                    &:not(.full-width) {
                        max-width: ${maxWidth}px;
                        padding-left: ${containerPadding[index] || containerPadding[0]}px;
                        padding-right: ${containerPadding[index] || containerPadding[0]}px;
                        margin-right: auto;
                        margin-left: auto;
                    }
                    &.full-width {
                        max-width: 100%;
                        margin-right: 0;
                        margin-left: 0;
                        padding-left: 0;
                        padding-right: 0;
                    }
                `,
        );
        return css`
            ${breakpointMediaQueries(theme, containerStyles)}
        `;
    }}
`;

export const linkSizeVariants = {
    small: {
        size: fontConfig.fontSize.xSmall,
        lineHeight: fontConfig.lineHeight.xSmall,
        iconSize: 12,
    },
    medium: {
        size: fontConfig.fontSize.small,
        lineHeight: fontConfig.lineHeight.small,
        iconSize: 14,
    },
    large: {
        size: fontConfig.fontSize.regular,
        lineHeight: fontConfig.lineHeight.regular,
        iconSize: 16,
    },
};

export const createLinkIconStyles = ({
    icon,
    linkStyles = {},
    sizeVariant = "small",
}: {
    icon?: React.ComponentType | string;
    linkStyles?: LinkPresentationProps;
    sizeVariant?: "small" | "medium" | "large";
}) => {
    const marginPosition = linkStyles?.icon?.position === "right" ? "margin-left" : "margin-right";
    return {
        ...mergeWithCss(
            {
                typographyProps: {
                    size: linkSizeVariants[sizeVariant].size,
                    lineHeight: linkSizeVariants[sizeVariant].lineHeight,
                    weight: fontConfig.weight.semiBold,
                    css: css`
                        letter-spacing: 0;
                    `,
                },
                icon: {
                    iconProps: {
                        src: icon,
                        size: linkSizeVariants[sizeVariant].iconSize,
                        css: css`
                            ${marginPosition}: 8px;
                        `,
                    },
                    position: linkStyles?.icon?.position,
                },
            },
            linkStyles,
        ),
    } as LinkPresentationProps;
};

export const styledBoxStyle = css`
    width: 100%;
    display: flex;
    padding: 12px;
    border: 1px solid ${color("common.border")};
    background-color: ${color("common.white")};
`;

// This Wrapper used For total display ( order total, cart total, ...)
export const StyledBox = styled(StyledWrapper)<{ borderRadius?: number }>`
    ${styledBoxStyle}
    ${({ borderRadius }) =>
        borderRadius &&
        css`
            border-radius: ${borderRadius}px;
        `}

    @media print {
        margin-top: 10px;
        max-width: 400px;
    }

    ${avoidBreakInsidePrintElement}

    ${injectCss}
`;

export const CenteringWrapper = styled(StyledWrapper)`
    min-height: 300px;
    display: flex;
    align-items: center;
    justify-content: center;

    ${LoadingSpinnerStyle} {
        margin: auto;
    }
`;

const productAddToCartButtonLoadingStyle = css`
    &[data-status="loading"]::before {
        content: "";
        width: 12px;
        height: 12px;
        margin-right: 8px;
        border: 2px solid ${color("common.ui10")};
        border-top-color: transparent;
        border-radius: 50%;
        animation: spinner 1s linear infinite;
    }

    @keyframes spinner {
        100% {
            transform: rotate(360deg);
        }
    }
`;

interface ModalButtonsActionStyles {
    buttonsGridItem?: GridItemProps;
    cancelButton?: ButtonPresentationProps;
    submitButton?: ButtonPresentationProps;
}

export const modalButtonsActionStyles: ModalButtonsActionStyles = {
    buttonsGridItem: {
        width: 12,
        css: css`
            display: flex;
            justify-content: flex-end;
            gap: 10px;
        `,
    },
    cancelButton: {
        variant: "secondary",
    },
    submitButton: {
        variant: "primary",
    },
};

export const getInputStyleWithIcon = (iconSrc = Search): TextFieldPresentationProps => {
    return {
        iconProps: {
            src: iconSrc,
        },
        cssOverrides: {
            formInputWrapper: css`
                & > ${IconWrapper}, & > ${FormFieldClickable} > ${IconWrapper} {
                    top: 0;
                    right: 0;
                    width: 34px;
                    height: 34px;
                    padding: 5px;
                    border-radius: 0;
                    background: ${color("secondary")};
                    border: 1px solid ${color("secondary")};
                    border-width: 1px 1px 1px 0;
                    transition: background ease-in-out ${getProp("theme.transition.duration.regular")}ms;

                    & svg {
                        margin: auto;
                    }
                }
                & > ${FormFieldClickable}.clearClickable > ${IconWrapper} {
                    background: transparent;
                    border: unset;
                }

                & > ${FormFieldClickable}.searchClickable:hover > ${IconWrapper} {
                    background: ${color("common.ui12")};
                }

                input:focus:not(:disabled) {
                    & + ${FormFieldClickable},& + ${IconWrapper} {
                        &:not(.searchClickable),
                        & > ${IconWrapper} {
                            border: 1px solid ${color("common.ui10")};
                            border-left: unset;
                        }
                        & + ${FormFieldClickable} > ${IconWrapper} {
                            border-color: ${color("common.ui10")};
                            border-left: unset;
                        }
                    }
                    & + ${FormFieldClickable}.clearClickable {
                        &,
                        & > ${IconWrapper} {
                            border: unset;
                        }
                    }
                }
                input[aria-invalid="true"]:not(:disabled) {
                    & + ${IconWrapper}, & + ${FormFieldClickable} > ${IconWrapper} {
                        border-color: ${color("danger")};
                    }
                }
                &&&& > input {
                    padding-right: 36px;
                }
            `,
            inputSelect: css``,
        },
    };
};
